import { useState, useEffect, useCallback, useRef } from 'react';
import { auditApi, type UnifiedAuditEvent, type BarcodeTimelineResponse, type BarcodeTimelineEvent } from '@/lib/api/audit';
import { formatToEST } from '@/lib/utils/date-format';

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  RemoveItem:       { label: 'Remove Item',       color: 'bg-red-900 text-red-200' },
  BulkRemoveItems:  { label: 'Bulk Remove',        color: 'bg-red-900 text-red-200' },
  ClearLocation:    { label: 'Clear Location',     color: 'bg-orange-900 text-orange-200' },
  BulkClearLocation:{ label: 'Bulk Clear Loc.',    color: 'bg-orange-900 text-orange-200' },
  BulkSetStatus:    { label: 'Bulk Set Status',    color: 'bg-purple-900 text-purple-200' },
  BulkAssignSort:   { label: 'Bulk Assign Sort',   color: 'bg-blue-900 text-blue-200' },
  Resequence:       { label: 'Resequence',         color: 'bg-blue-900 text-blue-200' },
  CreateUser:       { label: 'Create User',        color: 'bg-green-900 text-green-200' },
  UpdateUser:       { label: 'Update User',        color: 'bg-yellow-900 text-yellow-200' },
  DeleteUser:       { label: 'Delete User',        color: 'bg-red-900 text-red-200' },
  ChangePassword:   { label: 'Change Password',    color: 'bg-yellow-900 text-yellow-200' },
  ApproveManifest:  { label: 'Approve Manifest',   color: 'bg-green-900 text-green-200' },
  RejectManifest:   { label: 'Reject Manifest',    color: 'bg-red-900 text-red-200' },
  DeleteBarcode:    { label: 'Delete Barcode',     color: 'bg-orange-900 text-orange-200' },
  DeleteScan:       { label: 'Delete Scan',        color: 'bg-orange-900 text-orange-200' },
};

const EVENT_TYPE_CONFIG: Record<string, { label: string; dotColor: string; badgeColor: string }> = {
  Scan:     { label: 'Scan',     dotColor: 'bg-cyan-500',   badgeColor: 'bg-cyan-900 text-cyan-200' },
  Movement: { label: 'Movement', dotColor: 'bg-amber-500',  badgeColor: 'bg-amber-900 text-amber-200' },
  Pick:     { label: 'Pick',     dotColor: 'bg-green-500',  badgeColor: 'bg-green-900 text-green-200' },
  Admin:    { label: 'Admin',    dotColor: 'bg-purple-500', badgeColor: 'bg-purple-900 text-purple-200' },
};

const STATUS_COLORS: Record<string, string> = {
  Active:    'bg-green-900 text-green-200',
  Assigned:  'bg-yellow-900 text-yellow-200',
  Picked:    'bg-amber-900 text-amber-200',
  Relocated: 'bg-blue-900 text-blue-200',
  Removed:   'bg-red-900 text-red-200',
};

const SOURCE_CONFIG: Record<string, { label: string; badgeColor: string }> = {
  Admin:    { label: 'Admin',    badgeColor: 'bg-purple-900 text-purple-200' },
  Scan:     { label: 'Scan',     badgeColor: 'bg-cyan-900 text-cyan-200' },
  Movement: { label: 'Movement', badgeColor: 'bg-amber-900 text-amber-200' },
};

function unifiedActionLabel(event: UnifiedAuditEvent): string {
  if (event.sourceType === 'Admin') return ACTION_LABELS[event.action]?.label ?? event.action;
  if (event.sourceType === 'Scan') return 'Scanned';
  return 'Location Changed';
}

function unifiedActionStyle(event: UnifiedAuditEvent): string {
  if (event.sourceType === 'Admin') return ACTION_LABELS[event.action]?.color ?? 'bg-gray-700 text-gray-300';
  if (event.sourceType === 'Scan') return 'bg-cyan-900 text-cyan-200';
  return 'bg-amber-900 text-amber-200';
}

// ─── Event Detail Modal ───────────────────────────────────────────────────────

function EventDetailModal({ event, onClose }: { event: UnifiedAuditEvent; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: 'Timestamp', value: <span className="font-mono">{formatToEST(event.timestamp)}</span> },
    { label: 'User',      value: event.userName },
    {
      label: 'Source',
      value: (
        <span className={`px-2 py-0.5 text-xs rounded ${SOURCE_CONFIG[event.sourceType]?.badgeColor ?? 'bg-gray-700 text-gray-300'}`}>
          {SOURCE_CONFIG[event.sourceType]?.label ?? event.sourceType}
        </span>
      ),
    },
    {
      label: 'Action',
      value: (
        <span className={`px-2 py-0.5 text-xs rounded ${unifiedActionStyle(event)}`}>
          {unifiedActionLabel(event)}
        </span>
      ),
    },
    event.barcodeValue ? { label: 'Barcode',     value: <span className="font-mono">{event.barcodeValue}</span> } : null,
    event.manifestName ? { label: 'Manifest',    value: event.manifestName } : null,
    event.entityType   ? { label: 'Entity Type', value: event.entityType } : null,
    event.entityId     ? { label: 'Entity ID',   value: <span className="font-mono">{event.entityId}</span> } : null,
    { label: 'Details', value: <span className="whitespace-pre-wrap break-words">{event.details || '—'}</span> },
  ].filter(Boolean) as { label: string; value: React.ReactNode }[];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-gray-800 rounded-lg border border-gray-700 shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold">Event Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {rows.map(({ label, value }) => (
            <div key={label} className="grid grid-cols-[7rem_1fr] gap-3 text-sm">
              <span className="text-gray-400 pt-0.5">{label}</span>
              <span className="text-white">{value}</span>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-gray-700 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Audit Log Tab ────────────────────────────────────────────────────────────

function AuditLogTab() {
  const [events, setEvents] = useState<UnifiedAuditEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState<UnifiedAuditEvent | null>(null);

  const [sourceFilter, setSourceFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');

  const PAGE_SIZE = 50;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await auditApi.getUnified({
        source: sourceFilter || undefined,
        from: fromFilter || undefined,
        to: toFilter || undefined,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
      });
      setEvents(data.items);
      setTotalCount(data.totalCount);
    } catch (err) {
      console.error('Failed to load unified audit log', err);
    } finally {
      setIsLoading(false);
    }
  }, [sourceFilter, fromFilter, toFilter, currentPage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFilterChange = () => setCurrentPage(1);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4 space-y-3">
        <h2 className="text-lg font-semibold">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); handleFilterChange(); }}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
            >
              <option value="">All Sources</option>
              {Object.entries(SOURCE_CONFIG).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">From</label>
            <input
              type="datetime-local"
              step="1"
              value={fromFilter}
              onChange={(e) => { setFromFilter(e.target.value); handleFilterChange(); }}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">To</label>
            <input
              type="datetime-local"
              step="1"
              value={toFilter}
              onChange={(e) => { setToFilter(e.target.value); handleFilterChange(); }}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="px-6 py-3 bg-gray-700 flex justify-between items-center">
          <span className="text-sm text-gray-300">{totalCount} entries</span>
          <span className="text-sm text-gray-300">Page {currentPage} of {totalPages || 1}</span>
        </div>

        {isLoading ? (
          <div className="text-center py-10 text-gray-400">Loading...</div>
        ) : events.length === 0 ? (
          <div className="text-center py-10 text-gray-400">No entries found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Barcode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {events.map((event, i) => (
                <tr
                  key={i}
                  onClick={() => setSelectedEvent(event)}
                  className="hover:bg-gray-750 cursor-pointer"
                >
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    {formatToEST(event.timestamp)}
                  </td>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">{event.userName}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded whitespace-nowrap ${SOURCE_CONFIG[event.sourceType]?.badgeColor ?? 'bg-gray-700 text-gray-300'}`}>
                      {SOURCE_CONFIG[event.sourceType]?.label ?? event.sourceType}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded whitespace-nowrap ${unifiedActionStyle(event)}`}>
                      {unifiedActionLabel(event)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-300 font-mono text-xs whitespace-nowrap">
                    {event.barcodeValue ?? (
                      event.entityId
                        ? <span className="text-gray-500">{event.entityType} #{event.entityId.length > 12 ? event.entityId.substring(0, 12) + '…' : event.entityId}</span>
                        : <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300 max-w-xs truncate">
                    {event.details}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {selectedEvent && (
        <EventDetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}

// ─── Barcode Timeline Tab ─────────────────────────────────────────────────────

function TimelineEventRow({ event }: { event: BarcodeTimelineEvent }) {
  const cfg = EVENT_TYPE_CONFIG[event.eventType] ?? { label: event.eventType, dotColor: 'bg-gray-500', badgeColor: 'bg-gray-700 text-gray-300' };

  const actionLabel = event.eventType === 'Admin'
    ? (ACTION_LABELS[event.action]?.label ?? event.action)
    : event.action;

  return (
    <div className="flex gap-4 py-3">
      {/* Dot + connector */}
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${cfg.dotColor}`} />
        <div className="w-px flex-1 bg-gray-700 mt-1" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-2">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`px-2 py-0.5 text-xs rounded font-medium ${cfg.badgeColor}`}>
            {cfg.label}
          </span>
          <span className="text-sm font-medium text-white">{actionLabel}</span>
          <span className="text-xs text-gray-500">by {event.userName}</span>
          <span className="text-xs text-gray-500 ml-auto whitespace-nowrap">
            {formatToEST(event.timestamp)}
          </span>
        </div>
        <p className="text-sm text-gray-300">{event.details}</p>
      </div>
    </div>
  );
}

function BarcodeTimelineTab() {
  const [query, setQuery] = useState('');
  const [input, setInput] = useState('');
  const [result, setResult] = useState<BarcodeTimelineResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const search = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setQuery(trimmed);
    setIsLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const data = await auditApi.getBarcodeTimeline(trimmed);
      if (data.totalManifests === 0 && data.timeline.length === 0) {
        setNotFound(true);
      } else {
        setResult(data);
      }
    } catch {
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    search(input);
  };

  const { currentState } = result ?? {};

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="bg-gray-800 rounded-lg p-4">
        <h2 className="text-lg font-semibold mb-3">Search Barcode</h2>
        <form onSubmit={handleSubmit} className="flex gap-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter barcode value…"
            className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded focus:outline-none focus:border-blue-500 font-mono text-sm"
            autoFocus
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Searching…' : 'Search'}
          </button>
        </form>
      </div>

      {/* Not found */}
      {notFound && (
        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
          No records found for <span className="font-mono text-white">"{query}"</span>
        </div>
      )}

      {/* Results */}
      {result && (
        <>
          {/* Current state card */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Barcode</p>
                <p className="font-mono text-lg font-bold text-white">{result.barcodeValue}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-1">{result.timeline.length} events across {result.totalManifests} manifest{result.totalManifests !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {currentState ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-3 pt-3 border-t border-gray-700">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Status</p>
                  <span className={`px-2 py-0.5 text-xs rounded font-medium ${STATUS_COLORS[currentState.status] ?? 'bg-gray-700 text-gray-300'}`}>
                    {currentState.isDeleted ? 'Deleted' : currentState.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Manifest</p>
                  <p className="text-sm text-white truncate">{currentState.manifestName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Location</p>
                  <p className="text-sm text-white">{currentState.locationCode ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Sort ID 1</p>
                  <p className="text-sm text-white font-mono">{currentState.sortId1 ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Sort ID 2</p>
                  <p className="text-sm text-white font-mono">{currentState.sortId2 ?? '—'}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 mt-2">No inventory record found for this barcode.</p>
            )}
          </div>

          {/* Timeline */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-4">Timeline</h2>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mb-4 pb-4 border-b border-gray-700">
              {Object.entries(EVENT_TYPE_CONFIG).map(([key, cfg]) => (
                <span key={key} className="flex items-center gap-1.5 text-xs text-gray-400">
                  <span className={`inline-block w-2 h-2 rounded-full ${cfg.dotColor}`} />
                  {cfg.label}
                </span>
              ))}
            </div>

            {result.timeline.length === 0 ? (
              <p className="text-sm text-gray-400">No events recorded.</p>
            ) : (
              <div className="divide-y divide-gray-700/50">
                {result.timeline.map((event, i) => (
                  <TimelineEventRow key={i} event={event} />
                ))}
                {/* End cap */}
                <div className="flex gap-4 pt-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-gray-600 flex-shrink-0" />
                  </div>
                  <p className="text-xs text-gray-500 pb-1">End of timeline</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = 'log' | 'barcode';

export function AuditLogPage() {
  const [activeTab, setActiveTab] = useState<Tab>('log');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Audit Log</h1>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-700">
        <button
          onClick={() => setActiveTab('log')}
          className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'log'
              ? 'border-blue-500 text-white'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          Audit Log
        </button>
        <button
          onClick={() => setActiveTab('barcode')}
          className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === 'barcode'
              ? 'border-blue-500 text-white'
              : 'border-transparent text-gray-400 hover:text-gray-200'
          }`}
        >
          Barcode Timeline
        </button>
      </div>

      {activeTab === 'log' ? <AuditLogTab /> : <BarcodeTimelineTab />}
    </div>
  );
}
